import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Box,
  Grid,
  makeStyles,
  CircularProgress,
  IconButton,
  Typography,
} from "@material-ui/core";
import { Section } from "@microrealestate/commonui/components";
import { StoreContext } from "../../../landlord/src/store";
import { TextField } from "./TextField";
import useTranslation from "next-translate/useTranslation";
import { SelectAddressField } from "./SelectAddressField";
import axios from "axios";
import { NumberField } from "./NumberField";
import dynamic from "next/dynamic";
import { useField } from "formik";

const MapField = dynamic(() => import("./MapField"), {
  ssr: false,
});

export function AddressField({
  showMap = false,
  isDialog,
  disabled,
  prov,
  dist,
  subDist,
  vill,
  markerPosition,
  onPositionChange,
  required = true,
  ...props
}) {
  const { t } = useTranslation("common");
  const store = useContext(StoreContext);
  const [province, setProvince] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subDistricts, setSubDistricts] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(
    prov !== 0 ? prov : ""
  );
  const [selectedDistrict, setSelectedDistrict] = useState(
    dist !== 0 ? dist : ""
  );
  const [selectedSubDistrict, setSelectedSubDistrict] = useState(
    subDist !== 0 ? subDist : ""
  );
  const [selectedVillage, setSelectedVillage] = useState(
    vill !== 0 ? vill : ""
  );
  const [loading, setLoading] = useState(false); // State untuk loading
  const [position, setPosition] = useState(
    markerPosition
      ? {
          lat: markerPosition.lat,
          lng: markerPosition.lng ?? markerPosition.lon ?? 0,
        }
      : { lat: 0, lng: 0 }
  );
  const [isProvinceManual, setIsProvinceManual] = useState(false);
  const [isDistrictManual, setIsDistrictManual] = useState(false);
  const [isSubDistrictManual, setIsSubDistrictManual] = useState(false);
  const [isVillageManual, setIsVillageManual] = useState(false);

  const [field, meta, helpers] = useField("address.street1");
  const [zipField, zipMeta, zipHelpers] = useField("address.zipCode");
  const [provinceField, provinceMeta, provinceHelpers] =
    useField("address.province");
  const [districtField, districtMeta, districtHelpers] =
    useField("address.district");
  const [subDistrictField, subDistrictMeta, subDistrictHelpers] = useField(
    "address.subDistrict"
  );
  const [villageField, villageMeta, villageHelpers] =
    useField("address.village");
  const [latField, , latHelpers] = useField("address.lat");
  const [lonField, , lonHelpers] = useField("address.lon");

  const useStyles = makeStyles({
    no_arrow: {
      "& input[type=number]": {
        "-moz-appearance": "textfield",
      },
      "& input[type=number]::-webkit-outer-spin-button": {
        "-webkit-appearance": "none",
        margin: 0,
      },
      "& input[type=number]::-webkit-inner-spin-button": {
        "-webkit-appearance": "none",
        margin: 0,
      },
    },
  });
  const classes = useStyles();

  // Function to capitalize the first letter of each word and handle extra spaces
  const capitalizeFirstLetter = (string) =>
    string
      .toLowerCase()
      .replace(/\s+/g, " ") // Remove extra spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word

  const sortedData = (field) =>
    field.sort((a, b) => a.nama.localeCompare(b.nama));

  useEffect(() => {
    // Load province data
    const fetchProvinceData = async () => {
      if (province.length > 0) return;

      setLoading(true); // Start loading
      try {
        const province = await store.address.province();
        const responseProvince = province;
        const sortedProvince = sortedData(responseProvince.data);

        const formattedProvince = sortedProvince.map((province) => {
          let provinceName = capitalizeFirstLetter(province.nama);

          // Correct the specific case of "P A P U A"
          if (provinceName === "P A P U A") {
            provinceName = "Papua";
          }

          return {
            id: province.id,
            label: provinceName,
            value: `${province.id}-${provinceName}`,
          };
        });

        setProvince(formattedProvince);

        // Only fetch districts, subdistricts, and villages if needed
        if (prov !== 0) {
          const [responseDistrict, responseSubDistrict, responseVillage] =
            await Promise.all([
              store.address.districts(prov),
              store.address.subdistricts(dist),
              store.address.villages(subDist),
            ]);

          const formattedDistrict = sortedData(responseDistrict.data).map(
            (district) => ({
              id: district.id,
              label: capitalizeFirstLetter(district.nama),
              value: `${district.id}-${capitalizeFirstLetter(district.nama)}`,
            })
          );

          const formattedSubDistrict = sortedData(responseSubDistrict.data).map(
            (subDistrict) => ({
              id: subDistrict.id,
              label: capitalizeFirstLetter(subDistrict.nama),
              value: `${subDistrict.id}-${capitalizeFirstLetter(
                subDistrict.nama
              )}`,
            })
          );

          const formattedVillage = sortedData(responseVillage.data).map(
            (village) => ({
              id: village.id,
              label: capitalizeFirstLetter(village.nama),
              value: `${village.id}-${capitalizeFirstLetter(village.nama)}`,
            })
          );

          setDistricts(formattedDistrict);
          setSubDistricts(formattedSubDistrict);
          setVillages(formattedVillage);
        }
      } catch (error) {
        console.error("Error fetching provinces:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProvinceData();
  }, [prov, dist, subDist, store.address]);

  useEffect(() => {
    if (markerPosition) {
      setPosition({
        lat: markerPosition.lat,
        lng: markerPosition.lng ?? markerPosition.lon ?? 0,
      });
    }
  }, [markerPosition]);

  const fetchData = async (address, addressCode, setter, errorMessage) => {
    setLoading(true);
    try {
      let response;
      if (address === "province") {
        response = await store.address.districts(addressCode);
      } else if (address === "districts") {
        response = await store.address.subdistricts(addressCode);
      } else if (address === "subdistricts") {
        response = await store.address.villages(addressCode);
      }

      const sortedData = response.data.sort((a, b) =>
        a.nama.localeCompare(b.nama)
      );

      const formattedData = sortedData.map((data) => ({
        id: data.id,
        label: capitalizeFirstLetter(data.nama),
        value: `${data.id}-${capitalizeFirstLetter(data.nama)}`,
      }));

      setter(formattedData);
    } catch (error) {
      console.error(errorMessage, error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoordinates = async (addressString) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          addressString
        )}&limit=1&countrycodes=id`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
    } catch (e) {}
    return null;
  };

  // Handle province selection
  const handleProvinceChange = async (selectedValue) => {
    setSelectedProvince(selectedValue.target.value);
    setSelectedDistrict("");
    setSelectedSubDistrict("");
    setSelectedVillage("");
    setSubDistricts([]);
    setVillages([]);
    districtHelpers.setValue("");
    subDistrictHelpers.setValue("");
    villageHelpers.setValue("");
    const [provinceCode] = selectedValue.target.value.split("-");
    fetchData(
      "province",
      provinceCode,
      setDistricts,
      "Error fetching districts:"
    );

    const provinceObj = province.find(
      (p) => p.value === selectedValue.target.value
    );
    if (provinceObj) {
      const addressString = `${provinceObj.label}, Indonesia`;
      const coords = await fetchCoordinates(addressString);
      if (coords) {
        handlePositionChange(coords);
      }
    }
  };

  // Handle district selection
  const handleDistrictChange = async (selectedValue) => {
    setSelectedDistrict(selectedValue.target.value);
    setSelectedSubDistrict("");
    setSelectedVillage("");
    setVillages([]);
    subDistrictHelpers.setValue("");
    villageHelpers.setValue("");
    const [districtCode] = selectedValue.target.value.split("-");
    fetchData(
      "districts",
      districtCode,
      setSubDistricts,
      "Error fetching sub-districts:"
    );

    const districtObj = districts.find(
      (d) => d.value === selectedValue.target.value
    );
    const provinceObj = province.find((p) => p.value === selectedProvince);
    if (districtObj && provinceObj) {
      const addressString = `${districtObj.label}, ${provinceObj.label}, Indonesia`;
      const coords = await fetchCoordinates(addressString);
      if (coords) {
        handlePositionChange(coords);
      }
    }
  };

  // Handle sub-district selection
  const handleSubDistrictChange = async (selectedValue) => {
    setSelectedSubDistrict(selectedValue.target.value);
    setSelectedVillage("");
    const [subDistrictCode] = selectedValue.target.value.split("-");
    fetchData(
      "subdistricts",
      subDistrictCode,
      setVillages,
      "Error fetching villages:"
    );

    const subDistrictObj = subDistricts.find(
      (s) => s.value === selectedValue.target.value
    );
    const districtObj = districts.find((d) => d.value === selectedDistrict);
    const provinceObj = province.find((p) => p.value === selectedProvince);
    if (subDistrictObj && districtObj && provinceObj) {
      const addressString = [
        cleanLabel(subDistrictObj?.label),
        cleanLabel(districtObj?.label),
        cleanLabel(provinceObj?.label),
        "Indonesia",
      ]
        .filter(Boolean)
        .join(", ");
      const coords = await fetchCoordinates(addressString);
      if (coords) {
        handlePositionChange(coords);
      }
    }
  };

  // Handle village selection
  const handleVillageChange = async (selectedValue) => {
    setSelectedVillage(selectedValue.target.value);

    const villageObj = villages.find(
      (v) => v.value === selectedValue.target.value
    );
    const subDistrictObj = subDistricts.find(
      (s) => s.value === selectedSubDistrict
    );
    const districtObj = districts.find((d) => d.value === selectedDistrict);
    const provinceObj = province.find((p) => p.value === selectedProvince);

    if (villageObj && subDistrictObj && districtObj && provinceObj) {
      const addressString = [
        cleanLabel(villageObj?.label),
        cleanLabel(subDistrictObj?.label),
        cleanLabel(districtObj?.label),
        cleanLabel(provinceObj?.label),
        "Indonesia",
      ]
        .filter(Boolean)
        .join(", ");
      const coords = await fetchCoordinates(addressString);
      if (coords) {
        handlePositionChange(coords);
      }
    }
  };

  const handlePositionChange = (newPosition) => {
    setPosition(newPosition);
    latHelpers.setValue(newPosition.lat);
    lonHelpers.setValue(newPosition.lng);

    if (typeof onPositionChange === "function") {
      onPositionChange(newPosition);
    }
  };

  function cleanLabel(label) {
    return label
      .replace(/^Kec\.?\s*/i, "")
      .replace(/^Kel\.?\s*/i, "")
      .replace(/^Kab\.?\s*/i, "")
      .replace(/^Kota\s*/i, "")
      .trim();
  }

  return (
    <Box {...props}>
      <Grid container spacing={2}>
        {isDialog ? (
          <>
            <Grid container spacing={2}>
              <Typography style={{ marginLeft: "10px", marginBottom: "15px" }}>
                {t("Select a location on the map to mark the correct place.")}
              </Typography>
              {showMap ? (
                <>
                  <Grid item xs={12} md={7}>
                    <MapField
                      markerPosition={position}
                      onPositionChange={handlePositionChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    {loading ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        marginTop={"10px"}
                        alignItems="center"
                        p={2}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <SelectAddressField
                            key={provinceField.value}
                            label={t("Province") + (required ? " *" : "")}
                            name="address.province"
                            values={province}
                            value={
                              isProvinceManual
                                ? selectedProvince
                                : provinceField.value
                            }
                            onChange={handleProvinceChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <SelectAddressField
                            key={districtField.value}
                            label={t("District") + (required ? " *" : "")}
                            name="address.district"
                            values={districts}
                            value={
                              isDistrictManual
                                ? selectedDistrict
                                : districtField.value
                            }
                            onChange={handleDistrictChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <SelectAddressField
                            key={subDistrictField.value}
                            label={t("SubDistrict") + (required ? " *" : "")}
                            name="address.subDistrict"
                            values={subDistricts}
                            value={
                              isSubDistrictManual
                                ? selectedSubDistrict
                                : subDistrictField.value
                            }
                            onChange={handleSubDistrictChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <SelectAddressField
                            label={t("Village") + (required ? " *" : "")}
                            name="address.village"
                            values={villages}
                            value={selectedVillage}
                            onChange={handleVillageChange}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  {loading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      marginTop={"10px"}
                      alignItems="center"
                      p={2}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <SelectAddressField
                          key={provinceField.value}
                          label={t("Province") + (required ? " *" : "")}
                          name="address.province"
                          values={province}
                          value={
                            isProvinceManual
                              ? selectedProvince
                              : provinceField.value
                          }
                          onChange={handleProvinceChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <SelectAddressField
                          key={districtField.value}
                          label={t("District") + (required ? " *" : "")}
                          name="address.district"
                          values={districts}
                          value={
                            isDistrictManual
                              ? selectedDistrict
                              : districtField.value
                          }
                          onChange={handleDistrictChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <SelectAddressField
                          key={subDistrictField.value}
                          label={t("SubDistrict") + (required ? " *" : "")}
                          name="address.subDistrict"
                          values={subDistricts}
                          value={
                            isSubDistrictManual
                              ? selectedSubDistrict
                              : subDistrictField.value
                          }
                          onChange={handleSubDistrictChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <SelectAddressField
                          label={t("Village") + (required ? " *" : "")}
                          name="address.village"
                          values={villages}
                          value={selectedVillage}
                          onChange={handleVillageChange}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  style={{
                    marginLeft: "10px",
                    marginBottom: "15px",
                    marginTop: "20px",
                  }}
                >
                  {t("Please Provide Your Address")}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <TextField
                  label={t("Street 1") + (required ? " *" : "")}
                  name="address.street1"
                  value={field.value}
                  onChange={(event) => {
                    helpers.setValue(event.target.value);
                  }}
                  inputProps={{ style: { textTransform: "capitalize" } }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <NumberField
                  className={classes.no_arrow}
                  label={t("Zip code") + (required ? " *" : "")}
                  name="address.zipCode"
                  value={zipField.value}
                  onChange={(event) => {
                    zipHelpers.setValue(event.target.value);
                  }}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            {showMap ? (
              <>
                <Grid item xs={12} md={6}>
                  {loading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      style={{ height: "400px" }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <MapField
                      markerPosition={position}
                      onPositionChange={handlePositionChange}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label={t("Street 1") + (required ? " *" : "")}
                        name="address.street1"
                        value={field.value}
                        onChange={(event) => {
                          helpers.setValue(event.target.value);
                        }}
                        inputProps={{ style: { textTransform: "capitalize" } }}
                        disabled={disabled}
                      />
                      {meta.touched && meta.error ? (
                        <div style={{ color: "red" }}>{meta.error}</div>
                      ) : null}
                    </Grid>
                    <Grid item xs={12}>
                      <NumberField
                        className={classes.no_arrow}
                        label={t("Zip code") + (required ? " *" : "")}
                        name="address.zipCode"
                        value={zipField.value}
                        onChange={(event) => {
                          zipHelpers.setValue(event.target.value);
                        }}
                        disabled={disabled}
                      />
                    </Grid>
                  </Grid>
                  {loading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      marginTop={"20px"}
                      alignItems="center"
                      p={2}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <SelectAddressField
                          key={provinceField.value}
                          label={t("Province") + (required ? " *" : "")}
                          name="address.province"
                          values={province}
                          value={
                            isProvinceManual
                              ? selectedProvince
                              : provinceField.value
                          }
                          onChange={handleProvinceChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SelectAddressField
                          key={districtField.value}
                          label={t("District") + (required ? " *" : "")}
                          name="address.district"
                          values={districts}
                          value={
                            isDistrictManual
                              ? selectedDistrict
                              : districtField.value
                          }
                          onChange={handleDistrictChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SelectAddressField
                          key={subDistrictField.value}
                          label={t("SubDistrict") + (required ? " *" : "")}
                          name="address.subDistrict"
                          values={subDistricts}
                          value={
                            isSubDistrictManual
                              ? selectedSubDistrict
                              : subDistrictField.value
                          }
                          onChange={handleSubDistrictChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SelectAddressField
                          label={t("Village") + (required ? " *" : "")}
                          name="address.village"
                          values={villages}
                          value={selectedVillage}
                          onChange={handleVillageChange}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label={t("Street 1") + (required ? " *" : "")}
                      name="address.street1"
                      value={field.value}
                      onChange={(event) => {
                        helpers.setValue(event.target.value);
                      }}
                      inputProps={{ style: { textTransform: "capitalize" } }}
                      disabled={disabled}
                    />
                    {meta.touched && meta.error ? (
                      <div style={{ color: "red" }}>{meta.error}</div>
                    ) : null}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <NumberField
                      className={classes.no_arrow}
                      label={t("Zip code") + (required ? " *" : "")}
                      name="address.zipCode"
                      value={zipField.value}
                      onChange={(event) => {
                        zipHelpers.setValue(event.target.value);
                      }}
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
                {loading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    marginTop={"20px"}
                    alignItems="center"
                    p={2}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <SelectAddressField
                        key={provinceField.value}
                        label={t("Province") + (required ? " *" : "")}
                        name="address.province"
                        values={province}
                        value={
                          isProvinceManual
                            ? selectedProvince
                            : provinceField.value
                        }
                        onChange={handleProvinceChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectAddressField
                        key={districtField.value}
                        label={t("District") + (required ? " *" : "")}
                        name="address.district"
                        values={districts}
                        value={
                          isDistrictManual
                            ? selectedDistrict
                            : districtField.value
                        }
                        onChange={handleDistrictChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectAddressField
                        key={subDistrictField.value}
                        label={t("SubDistrict") + (required ? " *" : "")}
                        name="address.subDistrict"
                        values={subDistricts}
                        value={
                          isSubDistrictManual
                            ? selectedSubDistrict
                            : subDistrictField.value
                        }
                        onChange={handleSubDistrictChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectAddressField
                        label={t("Village") + (required ? " *" : "")}
                        name="address.village"
                        values={villages}
                        value={selectedVillage}
                        onChange={handleVillageChange}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
}
